import { useState, useEffect, useRef } from 'react';
import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getAnimation, hexToRgba } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';

export default function HotspotImageSection({ block = {}, primaryColor, secondaryColor, enableAnimations, cardStyle, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);
    const variant = config.variant || 'numbered';
    const hotspots = content.hotspots || [];
    const [activeHotspot, setActiveHotspot] = useState(null);
    const containerRef = useRef(null);

    // Resolve image from media folders
    let mainImage = content.image_url || null;
    if (content.image_folder_id && content.image_file_id && mediaFolders[content.image_folder_id]) {
        const found = mediaFolders[content.image_folder_id].find(f => String(f.id) === String(content.image_file_id));
        if (found) mainImage = found.image_url;
    }

    // Click outside to close tooltip
    useEffect(() => {
        if (activeHotspot === null) return;
        const handler = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setActiveHotspot(null);
            }
        };
        document.addEventListener('mousedown', handler);
        document.addEventListener('touchstart', handler);
        return () => {
            document.removeEventListener('mousedown', handler);
            document.removeEventListener('touchstart', handler);
        };
    }, [activeHotspot]);

    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle };

    const handleHotspotClick = (i) => {
        setActiveHotspot(activeHotspot === i ? null : i);
    };

    // Tooltip position: avoid overflow by adjusting horizontally
    const getTooltipPosition = (x, y) => {
        const style = {};
        // Vertical: show above if near bottom, below if near top
        if (y > 65) {
            style.bottom = '100%';
            style.marginBottom = '12px';
            style.caretPosition = 'bottom';
        } else {
            style.top = '100%';
            style.marginTop = '12px';
            style.caretPosition = 'top';
        }
        // Horizontal: center, but shift if near edges
        if (x < 20) {
            style.left = '0';
            style.transform = 'none';
            style.caretLeft = '16px';
        } else if (x > 80) {
            style.right = '0';
            style.transform = 'none';
            style.caretRight = '16px';
        } else {
            style.left = '50%';
            style.transform = 'translateX(-50%)';
            style.caretLeft = '50%';
            style.caretTransform = 'translateX(-50%)';
        }
        return style;
    };

    // ── Marker renderer ──
    const renderMarker = (hotspot, i) => {
        const isActive = activeHotspot === i;

        if (variant === 'dots') {
            return (
                <div className="relative">
                    {/* Pulse ring */}
                    <div className="absolute inset-0 rounded-full animate-ping"
                        style={{ backgroundColor: hexToRgba(s.accent, 0.3), animationDuration: '2s' }} />
                    {/* Dot */}
                    <div className={`w-4 h-4 md:w-3.5 md:h-3.5 rounded-full border-2 border-white shadow-lg transition-all duration-300 ${isActive ? 'scale-150' : 'scale-100'}`}
                        style={{
                            backgroundColor: s.accent,
                            boxShadow: isActive ? `0 0 0 4px ${hexToRgba(s.accent, 0.3)}` : '0 2px 4px rgba(0,0,0,0.3)',
                        }} />
                </div>
            );
        }

        if (variant === 'icons') {
            return (
                <div className={`w-9 h-9 md:w-8 md:h-8 rounded-full flex items-center justify-center text-white shadow-lg transition-all duration-300 ${isActive ? 'scale-125' : 'scale-100 hover:scale-110'}`}
                    style={{
                        backgroundColor: s.accent,
                        boxShadow: isActive ? `0 0 0 4px ${hexToRgba(s.accent, 0.3)}` : '0 2px 8px rgba(0,0,0,0.25)',
                    }}>
                    {hotspot.icon ? (
                        <span className="text-sm">{hotspot.icon}</span>
                    ) : (
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    )}
                </div>
            );
        }

        // Numbered (default)
        return (
            <div className={`w-9 h-9 md:w-8 md:h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-lg transition-all duration-300 ${isActive ? 'scale-125' : 'scale-100 hover:scale-110'}`}
                style={{
                    backgroundColor: s.accent,
                    boxShadow: isActive ? `0 0 0 4px ${hexToRgba(s.accent, 0.3)}` : '0 2px 8px rgba(0,0,0,0.25)',
                }}>
                {i + 1}
            </div>
        );
    };

    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />

                {/* Image container with hotspots */}
                <div ref={containerRef} className={`relative max-w-4xl mx-auto transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
                    {/* Base image */}
                    {mainImage ? (
                        <img src={mainImage} alt={content.title || ''} className="w-full rounded-xl shadow-lg object-contain" loading="lazy" />
                    ) : (
                        <div className="aspect-[16/9] rounded-xl flex items-center justify-center"
                            style={{ backgroundColor: hexToRgba(s.accent, 0.04) }}>
                            <svg className="w-20 h-20 opacity-15" style={{ color: s.accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                        </div>
                    )}

                    {/* Hotspot markers */}
                    {hotspots.map((hotspot, i) => {
                        const x = parseFloat(hotspot.x) || 0;
                        const y = parseFloat(hotspot.y) || 0;
                        const isActive = activeHotspot === i;
                        const tooltipPos = getTooltipPosition(x, y);
                        const caretOnTop = tooltipPos.caretPosition === 'top';

                        return (
                            <div key={i}
                                className="absolute z-10"
                                style={{
                                    left: `${x}%`,
                                    top: `${y}%`,
                                    transform: 'translate(-50%, -50%)',
                                }}>
                                {/* Clickable marker */}
                                <button
                                    onClick={() => handleHotspotClick(i)}
                                    className="relative cursor-pointer focus:outline-none"
                                    aria-label={hotspot.title || `Hotspot ${i + 1}`}>
                                    {renderMarker(hotspot, i)}
                                </button>

                                {/* Tooltip */}
                                {isActive && (hotspot.title || hotspot.description) && (
                                    <div className="absolute z-20 w-56 sm:w-64"
                                        style={{
                                            ...(tooltipPos.bottom !== undefined ? { bottom: tooltipPos.bottom, marginBottom: tooltipPos.marginBottom } : {}),
                                            ...(tooltipPos.top !== undefined ? { top: tooltipPos.top, marginTop: tooltipPos.marginTop } : {}),
                                            ...(tooltipPos.left !== undefined ? { left: tooltipPos.left } : {}),
                                            ...(tooltipPos.right !== undefined ? { right: tooltipPos.right } : {}),
                                            ...(tooltipPos.transform ? { transform: tooltipPos.transform } : {}),
                                        }}>
                                        {/* Caret */}
                                        <div className={`absolute w-3 h-3 rotate-45 ${caretOnTop ? '-top-1.5' : '-bottom-1.5'}`}
                                            style={{
                                                backgroundColor: s.dark ? '#1f2937' : '#ffffff',
                                                left: tooltipPos.caretLeft || '50%',
                                                right: tooltipPos.caretRight || undefined,
                                                transform: tooltipPos.caretTransform || 'translateX(-50%)',
                                                boxShadow: caretOnTop ? '-1px -1px 2px rgba(0,0,0,0.05)' : '1px 1px 2px rgba(0,0,0,0.05)',
                                            }} />

                                        {/* Card */}
                                        <div className="rounded-xl shadow-xl p-4 relative"
                                            style={{
                                                backgroundColor: s.dark ? '#1f2937' : '#ffffff',
                                                border: `1px solid ${s.dark ? 'rgba(255,255,255,0.1)' : '#e5e7eb'}`,
                                            }}>
                                            <div className="flex items-start gap-2">
                                                <span className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[10px] font-bold shrink-0 mt-0.5"
                                                    style={{ backgroundColor: s.accent }}>
                                                    {variant === 'icons' && hotspot.icon ? (
                                                        <span className="text-xs">{hotspot.icon}</span>
                                                    ) : (
                                                        i + 1
                                                    )}
                                                </span>
                                                <div className="flex-1 min-w-0">
                                                    {hotspot.title && (
                                                        <h4 className="text-sm font-bold leading-tight" style={{ color: s.text }}>
                                                            {hotspot.title}
                                                        </h4>
                                                    )}
                                                    {hotspot.description && (
                                                        <p className="text-xs mt-1 leading-relaxed" style={{ color: s.textSub }}>
                                                            {hotspot.description}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Legend list below image */}
                {hotspots.length > 0 && (
                    <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-w-4xl mx-auto">
                        {hotspots.map((hotspot, i) => (
                            <button key={i}
                                onClick={() => handleHotspotClick(i)}
                                className={`flex items-center gap-3 p-3 rounded-lg text-left transition-all duration-200 ${activeHotspot === i ? 'ring-2' : 'hover:opacity-80'}`}
                                style={{
                                    backgroundColor: activeHotspot === i ? hexToRgba(s.accent, 0.08) : (s.dark ? 'rgba(255,255,255,0.04)' : '#f9fafb'),
                                    ringColor: s.accent,
                                }}>
                                <span className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[11px] font-bold shrink-0"
                                    style={{ backgroundColor: activeHotspot === i ? s.accent : (s.dark ? 'rgba(255,255,255,0.15)' : '#d1d5db') }}>
                                    {variant === 'icons' && hotspot.icon ? (
                                        <span className="text-xs">{hotspot.icon}</span>
                                    ) : (
                                        i + 1
                                    )}
                                </span>
                                <span className="text-sm font-medium truncate" style={{ color: s.text }}>
                                    {hotspot.title || `Punto ${i + 1}`}
                                </span>
                            </button>
                        ))}
                    </div>
                )}
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
